import type { RouterConfig } from '@nuxt/schema'

const scrollContainerPositions = new Map<string, number>()

const getScrollContainer = () => {
  return document.querySelector<HTMLElement>('[data-scroll-container]')
}

const saveScrollContainerPosition = (routePath: string) => {
  const scrollContainer = getScrollContainer()
  if (!scrollContainer) return

  scrollContainerPositions.set(routePath, scrollContainer.scrollTop)
}

const restoreScrollContainerPosition = (
  routePath: string,
  defaultTop = 0,
) => {
  const scrollContainer = getScrollContainer()
  if (!scrollContainer) return false

  const top = scrollContainerPositions.get(routePath) ?? defaultTop

  scrollContainer.scrollTo({
    top,
    left: 0,
    behavior: 'auto',
  })

  return true
}

export default <RouterConfig>{
  scrollBehavior(to, from, savedPosition) {
    if (to.path !== from.path) {
      saveScrollContainerPosition(from.fullPath)
    }

    let scrollBehavior: any = null
    if (to && to.hash) {
      // Apply correct scroll padding from html element
      const top = getComputedStyle(document.documentElement).scrollPaddingTop
      scrollBehavior = {
        el: to.hash,
        top: +top.replace('px', ''),
        behavior: 'smooth',
      }
    } else if (!savedPosition) {
      scrollBehavior = {
        top: 0,
        left: 0,
        behavior: 'smooth',
      }
    }

    /**
     * Immediately apply scroll behavior if the user is navigating in the same page (i.e. anchor links).
     * Otherwise, wait for the page to finish loading.
     */
    if (to.path === from.path) {
      return scrollBehavior
    } else {
      const nuxtApp = useNuxtApp()
      return new Promise((resolve) => {
        nuxtApp.hooks.hookOnce('page:transition:finish', () => {
          if (savedPosition) {
            const restoredFromContainer = restoreScrollContainerPosition(
              to.fullPath,
              savedPosition?.top ?? 0,
            )

            if (restoredFromContainer && !to.hash) {
              resolve(false)
              return
            }
          }
          resolve(scrollBehavior ?? false)
        })
      })
    }
  },
}
